import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import {
  MAX_PORTFOLIO_IMAGES,
  providersService,
  type UpdatePortfolioImagePayload,
} from '../../shared/services/providers.service';
import type { PortfolioImage } from '../../shared/types';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { AddPortfolioImagesBulkDialog } from '../dashboard/AddPortfolioImagesBulkDialog';

export function AdminProviderPortfolioPage() {
  const { id: providerId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { hasRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const canEdit = hasRole('SUPER_ADMIN');

  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState<PortfolioImage | null>(null);
  const [deleting, setDeleting] = useState<PortfolioImage | null>(null);

  const detailKey = useMemo(
    () => ['admin', 'provider', providerId, 'detail'] as const,
    [providerId],
  );

  const detail = useQuery({
    queryKey: detailKey,
    // adminGet bypasses the APPROVED-only check of the public endpoint, so an
    // admin can manage portfolios of PENDING / REJECTED providers too.
    queryFn: () => providersService.adminGet(providerId!),
    enabled: !!providerId,
  });

  const remove = useMutation({
    mutationFn: (image: PortfolioImage) =>
      providersService.adminRemovePortfolioImage(providerId!, image.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey });
      notify.success(t('common.deleted', { defaultValue: 'Supprimé.' }));
      setDeleting(null);
    },
    onError: (err) => notify.error(translate(err)),
  });

  if (!providerId) {
    return null;
  }
  if (detail.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {translate(detail.error) ||
            t('admin.portfolioLoadError', {
              defaultValue: 'Provider introuvable.',
            })}
        </Alert>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/admin/providers')}
          sx={{ mt: 2 }}
        >
          {t('common.back', { defaultValue: 'Retour' })}
        </Button>
      </Container>
    );
  }

  const provider = detail.data;
  const portfolio = provider.portfolio ?? [];
  const remaining = MAX_PORTFOLIO_IMAGES - portfolio.length;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin/providers" underline="hover">
          {t('admin.providersTitle')}
        </Link>
        <Link
          component={RouterLink}
          to={`/providers/${provider.id}`}
          underline="hover"
        >
          {provider.businessName}
        </Link>
        <Typography color="text.primary">
          {t('admin.portfolioCta', { defaultValue: 'Portfolio' })}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {provider.businessName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              label={t('provider.portfolioCount', {
                count: portfolio.length,
                max: MAX_PORTFOLIO_IMAGES,
                defaultValue: `${portfolio.length} / ${MAX_PORTFOLIO_IMAGES} photo(s)`,
              })}
              color={portfolio.length > 0 ? 'primary' : 'default'}
            />
            <Chip
              size="small"
              variant="outlined"
              label={t('admin.portfolioSlotsLeft', {
                count: remaining,
                max: MAX_PORTFOLIO_IMAGES,
                defaultValue: `${remaining} / ${MAX_PORTFOLIO_IMAGES} restantes`,
              })}
            />
            {!canEdit && (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={t('admin.portfolioReadOnlyEdit', {
                  defaultValue: 'Édition réservée au SUPER_ADMIN',
                })}
              />
            )}
          </Stack>
        </Box>
        <Button
          startIcon={<PhotoLibraryRoundedIcon />}
          variant="contained"
          size="large"
          disabled={remaining <= 0}
          onClick={() => setBulkOpen(true)}
        >
          {t('provider.addMultiple', { defaultValue: 'Ajouter des photos' })}
        </Button>
      </Stack>

      {portfolio.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center' }}
        >
          <Typography color="text.secondary">
            {t('admin.portfolioEmpty', {
              defaultValue:
                'Ce prestataire n\'a pas encore de photos. Cliquez sur « Ajouter des photos ».',
            })}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {portfolio.map((img) => (
            <Grid item xs={6} sm={4} md={3} key={img.id}>
              <PortfolioTile
                image={img}
                businessName={provider.businessName}
                canEdit={canEdit}
                onEdit={() => setEditing(img)}
                onDelete={() => setDeleting(img)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AddPortfolioImagesBulkDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        currentCount={portfolio.length}
        targetProviderId={providerId}
        invalidateKeys={[detailKey as unknown as readonly unknown[]]}
      />

      {editing && (
        <EditPortfolioImageDialog
          open={Boolean(editing)}
          providerId={providerId}
          image={editing}
          onClose={() => setEditing(null)}
          invalidateKey={detailKey as unknown as readonly unknown[]}
        />
      )}

      <Dialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle>
          {t('admin.portfolioDeleteTitle', {
            defaultValue: 'Supprimer cette photo ?',
          })}
        </DialogTitle>
        <DialogContent>
          {deleting && (
            <Box
              component="img"
              src={deleting.imageUrl}
              alt={deleting.caption ?? provider.businessName}
              sx={{
                width: '100%',
                maxHeight: 240,
                objectFit: 'cover',
                borderRadius: 2,
                mb: 2,
              }}
            />
          )}
          <Typography color="text.secondary">
            {t('admin.portfolioDeleteConfirm', {
              defaultValue:
                'Cette action est irréversible. La photo sera retirée du profil du prestataire.',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={remove.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={remove.isPending}
            onClick={() => deleting && remove.mutate(deleting)}
          >
            {remove.isPending ? t('common.loading') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

function PortfolioTile({
  image,
  businessName,
  canEdit,
  onEdit,
  onDelete,
}: {
  image: PortfolioImage;
  businessName: string;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const priceLabel =
    image.priceCents != null
      ? new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: image.currency || 'EUR',
        }).format(image.priceCents / 100)
      : null;

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover .actions': { opacity: 1 },
      }}
    >
      <Box
        component="img"
        src={image.imageUrl}
        alt={image.caption ?? businessName}
        loading="lazy"
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />

      {priceLabel && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            px: 1.25,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
            color: 'common.white',
            fontWeight: 700,
            fontSize: '0.8125rem',
          }}
        >
          {priceLabel}
        </Box>
      )}

      {image.caption && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            px: 1,
            py: 0.75,
            background:
              'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
            color: 'common.white',
            fontSize: '0.75rem',
            pr: priceLabel ? 9 : 1,
          }}
        >
          {image.caption}
        </Box>
      )}

      <Stack
        className="actions"
        direction="row"
        spacing={0.5}
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          opacity: { xs: 1, sm: 0 },
          transition: 'opacity .15s ease',
        }}
      >
        {canEdit && (
          <Tooltip title={t('common.edit', { defaultValue: 'Éditer' })}>
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{
                bgcolor: 'rgba(0,0,0,0.55)',
                color: 'common.white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                width: 30,
                height: 30,
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('common.delete')}>
          <IconButton
            size="small"
            onClick={onDelete}
            sx={{
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'error.light',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              width: 30,
              height: 30,
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

function EditPortfolioImageDialog({
  open,
  providerId,
  image,
  onClose,
  invalidateKey,
}: {
  open: boolean;
  providerId: string;
  image: PortfolioImage;
  onClose: () => void;
  invalidateKey: readonly unknown[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [caption, setCaption] = useState(image.caption ?? '');
  const [price, setPrice] = useState(
    image.priceCents != null ? (image.priceCents / 100).toString() : '',
  );
  const [currency, setCurrency] = useState(image.currency ?? 'EUR');
  const [displayOrder, setDisplayOrder] = useState(image.displayOrder.toString());

  const update = useMutation({
    mutationFn: (payload: UpdatePortfolioImagePayload) =>
      providersService.adminUpdatePortfolioImage(providerId, image.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invalidateKey as unknown as string[] });
      notify.success(t('common.save'));
      onClose();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const handleSave = () => {
    const payload: UpdatePortfolioImagePayload = {};

    const trimmedCaption = caption.trim();
    if (trimmedCaption !== (image.caption ?? '')) {
      if (trimmedCaption === '') {
        payload.clearCaption = true;
      } else {
        payload.caption = trimmedCaption;
      }
    }

    const newOrder = Number(displayOrder);
    if (!Number.isNaN(newOrder) && newOrder !== image.displayOrder) {
      payload.displayOrder = newOrder;
    }

    const priceNum = price.trim() ? Number(price.replace(',', '.')) : null;
    const priceCentsNew = priceNum != null && !Number.isNaN(priceNum)
      ? Math.round(priceNum * 100)
      : null;
    const currencyNew = currency.trim().toUpperCase() || null;

    const priceChanged = priceCentsNew !== (image.priceCents ?? null);
    const currencyChanged = currencyNew !== (image.currency ?? null);

    if (priceChanged || currencyChanged) {
      if (priceCentsNew == null) {
        payload.clearPrice = true;
      } else if (!currencyNew || currencyNew.length !== 3) {
        notify.error(
          t('admin.portfolioCurrencyRequired', {
            defaultValue: 'La devise (3 lettres) est requise quand un prix est défini.',
          }),
        );
        return;
      } else {
        payload.priceCents = priceCentsNew;
        payload.currency = currencyNew;
      }
    }

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    update.mutate(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !update.isPending && onClose()}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
    >
      <DialogTitle>
        {t('admin.portfolioEditTitle', { defaultValue: 'Éditer la photo' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Box
            component="img"
            src={image.imageUrl}
            alt={caption || 'preview'}
            sx={{
              width: '100%',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
          <TextField
            label={t('provider.caption')}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t('provider.captionPlaceholder')}
            fullWidth
            inputProps={{ maxLength: 280 }}
            helperText={`${caption.length}/280`}
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              label={t('provider.price')}
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={t('provider.priceOptional')}
              inputProps={{ min: 0, step: '0.01' }}
              fullWidth
            />
            <TextField
              label={t('provider.currency')}
              value={currency}
              onChange={(e) =>
                setCurrency(e.target.value.toUpperCase().slice(0, 3))
              }
              sx={{ width: 110 }}
              disabled={!price}
            />
          </Stack>
          <TextField
            label={t('admin.portfolioOrder', {
              defaultValue: 'Ordre d\'affichage',
            })}
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={update.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={update.isPending}
        >
          {update.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
